import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useScrolled } from "@/hooks/useReveal";
import { useActiveSection } from "@/hooks/useActiveSection";
import { useOpeningStatus } from "@/hooks/useOpeningStatus";
import SectionLink from "@/components/base/SectionLink";

interface NavItem {
  label: string;
  hash: string;
  to?: string;
}

const links: NavItem[] = [
  { label: "Domov", hash: "domov" },
  { label: "O nas", hash: "o-nas" },
  { label: "Ponudba", hash: "ponudba" },
  { label: "Galerija", hash: "galerija" },
  { label: "Kontakt", hash: "kontakt" },
];

const sectionIds = ["domov", "o-nas", "ponudba", "galerija", "kontakt"];

interface NavLinkProps {
  hash: string;
  to?: string;
  className: string;
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
}

function NavLink({ hash, to, className, children, onClick, active }: NavLinkProps) {
  const { pathname } = useLocation();
  const ariaCurrent = active ? "true" : undefined;

  if (pathname === "/") {
    return (
      <a href={`#${hash}`} className={className} onClick={onClick} aria-current={ariaCurrent}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to ?? `/#${hash}`} className={className} onClick={onClick} aria-current={ariaCurrent}>
      {children}
    </Link>
  );
}

export default function Navbar() {
  const scrolled = useScrolled(60);
  const activeId = useActiveSection(sectionIds);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const { isOpen, statusText } = useOpeningStatus();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const jeAktiven = (link: NavItem) => {
    if (link.to) {
      return isHome ? activeId === link.hash : location.pathname === link.to;
    }
    return isHome && activeId === link.hash;
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ease-smooth ${
          scrolled
            ? "border-b border-background-300/70 bg-background-50"
            : "border-b border-background-50/10 bg-transparent"
        }`}
      >
        <div className="w-full px-5 md:px-8 lg:px-12">
          <div className="flex h-[76px] items-center justify-between md:h-[84px]">
            <NavLink
              hash="domov"
              onClick={() => setMenuOpen(false)}
              className="group flex items-center gap-2.5 transition-opacity duration-500 hover:opacity-90 md:gap-3"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/b6a5fe69-5197-42cf-a97a-adcd094ddbaa.png"
                alt="Logotip Bar Stanka Ptuj"
                className="h-[40px] w-[40px] shrink-0 object-contain md:h-[46px] md:w-[46px]"
              />
              <span className="flex flex-col justify-center">
                <span
                  className={`font-label text-[20px] font-semibold uppercase leading-none tracking-[0.14em] transition-colors duration-500 md:text-[24px] ${
                    scrolled ? "brand-neon-red" : "brand-neon-red-bright"
                  }`}
                >
                  STANKA
                </span>
                <span className="mt-[5px] flex items-center gap-2 leading-none md:mt-[6px]">
                  <span
                    className={`font-label text-[10px] font-semibold uppercase tracking-[0.32em] transition-colors duration-500 md:text-[11px] ${
                      scrolled ? "brand-neon-white" : "brand-neon-white-bright"
                    }`}
                  >
                    BAR
                  </span>
                  <span
                    className={`h-[3px] w-[3px] rounded-full transition-colors duration-500 ${
                      scrolled ? "bg-accent-600" : "bg-accent-400"
                    }`}
                  />
                  <span
                    className={`font-label text-[9px] font-medium uppercase tracking-[0.3em] transition-colors duration-500 md:text-[10px] ${
                      scrolled ? "text-foreground-600" : "text-accent-300"
                    }`}
                  >
                    Ptuj
                  </span>
                </span>
              </span>
            </NavLink>

            <nav className="hidden items-center gap-9 lg:flex">
              {links.map((link) => {
                const isActive = jeAktiven(link);

                return (
                  <NavLink
                    key={link.hash}
                    hash={link.hash}
                    to={link.to}
                    active={isActive}
                    className={`group relative font-label text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                      scrolled
                        ? isActive
                          ? "text-secondary-600"
                          : "text-foreground-700 hover:text-secondary-600"
                        : isActive
                          ? "text-accent-300"
                          : "text-background-100/90 hover:text-accent-300"
                    }`}
                  >
                    {link.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-px transition-all duration-500 ease-smooth group-hover:w-full ${
                        isActive ? "w-full" : "w-0"
                      } ${scrolled ? "bg-secondary-500" : "bg-accent-400"}`}
                    />
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex items-center gap-2.5 md:gap-3">
              <SectionLink
                hash="kontakt"
                className={`mr-1 hidden items-center gap-2 rounded-full border px-3.5 py-2 font-label text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 xl:inline-flex ${
                  scrolled
                    ? "border-background-300 text-foreground-700 hover:border-secondary-400 hover:text-secondary-600"
                    : "border-background-50/30 text-background-100/85 hover:border-accent-300 hover:text-accent-200"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    isOpen ? "bg-accent-500" : "bg-background-400"
                  }`}
                />
                {statusText}
              </SectionLink>

              <SectionLink
                hash="kontakt"
                className={`btn-base hidden md:inline-flex !px-6 !py-3 !text-[11px] ${
                  scrolled
                    ? "bg-primary-900 text-background-50 hover:bg-secondary-600"
                    : "border-background-50/60 bg-background-50/10 text-background-50 hover:border-accent-300 hover:bg-accent-500 hover:text-primary-950"
                }`}
              >
                Obiščite nas
              </SectionLink>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Odpri meni"
                aria-expanded={menuOpen}
                className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition-colors duration-300 lg:hidden ${
                  scrolled
                    ? "border-background-300 text-foreground-900"
                    : "border-background-50/40 text-background-50"
                }`}
              >
                <i className="ri-menu-3-line text-xl" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] lg:hidden ${menuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-primary-950/70 transition-opacity duration-500 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMenuOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 flex h-full w-[88%] max-w-[360px] flex-col overflow-y-auto overscroll-contain bg-primary-900 px-6 pb-[max(28px,env(safe-area-inset-bottom))] pt-6 transition-transform duration-500 ease-smooth ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5">
              <img
                src="https://public.readdy.ai/ai/img_res/b6a5fe69-5197-42cf-a97a-adcd094ddbaa.png"
                alt="Logotip Bar Stanka Ptuj"
                className="h-10 w-10 object-contain"
              />
              <span className="flex flex-col justify-center">
                <span className="brand-neon-red-bright font-label text-[22px] font-semibold uppercase leading-none tracking-[0.14em]">
                  STANKA
                </span>
                <span className="brand-neon-white-bright mt-[5px] font-label text-[10px] font-semibold uppercase leading-none tracking-[0.32em]">
                  Bar
                </span>
              </span>
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Zapri meni"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-50/25 text-background-50"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <nav className="mt-10 flex flex-col gap-1">
            {links.map((link, index) => (
              <NavLink
                key={link.hash}
                hash={link.hash}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="group flex items-center justify-between border-b border-background-50/10 py-3.5"
              >
                <span className="font-heading text-[22px] text-background-50 transition-colors duration-300 group-hover:text-accent-300">
                  {link.label}
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-px w-4 bg-background-50/20" />
                  <span className="font-label text-[10px] tracking-[0.2em] text-accent-500">
                    0{index + 1}
                  </span>
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto">
            <p className="font-label text-[10px] uppercase tracking-[0.22em] text-accent-500">
              Zadružni trg 12D
            </p>
            <p className="mt-1 text-sm text-background-100/80">2250 Ptuj, Slovenija</p>
            <SectionLink
              hash="kontakt"
              onClick={() => setMenuOpen(false)}
              className="btn-base mt-6 w-full bg-accent-500 text-primary-950 hover:bg-background-50"
            >
              Obiščite nas
            </SectionLink>
          </div>
        </div>
      </div>
    </>
  );
}